import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import tinycolor from "tinycolor2";

// API/Components
import axiosInstance from '../components/axios';
import obvmConfig from '../config';
import { dictToQueryString, formatDateTime } from '../components/enums';
import {
	ALARM_CATEGORY,
	FilterNumToEventTypes,
	AlarmEventToStatusText,
	EventTypeToColor,
} from '../components/alarm_enums';

// Material UI
import {
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Switch,
	Toolbar,
	Typography,
	Table,
	TableContainer,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TextField,
	Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import FilterListIcon from '@mui/icons-material/FilterList';
// import RefreshIcon from '@mui/icons-material/Refresh';	

export default function Events() {	
	const [events, setEvents] = useState([]);
	const [moreData, setMoreData] = useState(true);
	const [apiPage, setApiPage] = useState(1); // Page number for the API call
	
	// Save filtering states and colorization
	const [hierarchy, setHierarchy] = useState({});
	const [nameFiltering, setNameFiltering] = useState({
		search: '',
		organization: '',
		region: '',
		facility: '',
		building: '',
		ack_exclude: false,
		clear_exclude: false,
	});
	const [typeFiltering, setTypeFiltering] = useState({
		system_critical:	{ enabled: true, name: 'System Critical', filter: ALARM_CATEGORY.SYSTEM_CRITICAL },
		equipment:			{ enabled: true, name: 'Equipment', filter: ALARM_CATEGORY.EQUIPMENT },
		temperature:		{ enabled: true, name: 'Temperature', filter: ALARM_CATEGORY.TEMP },
		temp_diff:			{ enabled: true, name: 'Temp. Differential', filter: ALARM_CATEGORY.TEMP_DIFF },
		// unit_volt_crit:		{ enabled: true, name: 'Unit Voltage Critical', filter: ALARM_CATEGORY.UNIT_VOLT_CRIT },
		// unit_imped_crit:	{ enabled: true, name: 'Unit Impedance Critical', filter: ALARM_CATEGORY.UNIT_IMPED_CRIT },
		// unit_volt_maint:	{ enabled: true, name: 'Unit Voltage Maintenance', filter: ALARM_CATEGORY.UNIT_VOLT_MAINT },
		// unit_imped_maint:	{ enabled: true, name: 'Unit Impedance Maintenance', filter: ALARM_CATEGORY.UNIT_IMPED_MAINT },
		unit_voltage:		{ enabled: true, name: 'Unit Voltage', filter: ALARM_CATEGORY.UNIT_VOLT_CRIT },
		unit_impedance:		{ enabled: true, name: 'Unit Impedance', filter: ALARM_CATEGORY.UNIT_IMPED_CRIT },
		discharge:			{ enabled: true, name: 'Discharge', filter: ALARM_CATEGORY.DISCHARGE },
		abort:				{ enabled: true, name: 'Abort', filter: ALARM_CATEGORY.ABORT },
		aux:				{ enabled: true, name: 'Aux Inputs', filter: ALARM_CATEGORY.AUX },
		polling:			{ enabled: true, name: 'Polling', filter: ALARM_CATEGORY.POLLING },
	});
	const [isColorized, setIsColorized] = useState(true);

	// Dialog state management
	const [nameDialogOpen, setNameDialogOpen] = useState(false);
	const [typeDialogOpen, setTypeDialogOpen] = useState(false);

	const navigate = useNavigate();

	const eventsPerPage = obvmConfig.EVENTS.EVENTS_PER_PAGE;

	// Get information about the locations' organization hierarchy
	useEffect(() => {
		axiosInstance.get(`/hierarchy/`).then(response => {
			setHierarchy(response.data);
		});
	}, []);
	
	useEffect(() => {
		getEventData(true);
	}, [nameFiltering, typeFiltering]);

	// Return true if all filters are active
	function allFiltersActive() {
		for (let key in typeFiltering) {
			if (!typeFiltering[key].enabled) {
				return false;
			}
		}

		return true;
	}

	// Returns a list of all active filters
	function getActiveFilterAlarmTypes() {
		let activeFilters = [];

		Object.keys(typeFiltering).forEach((key) => {
			if (typeFiltering[key].enabled) {
				activeFilters.push(FilterNumToEventTypes(typeFiltering[key].filter));
			}
		});

		return activeFilters;
	}

	// function refreshEvents() {
	// 	setApiPage(1);
	// 	setMoreData(true);
	// 	getEventData(true);
	// }

	// Get event data from the API
	function getEventData(overwrite = true) {

		// Only attempt to get new data if there is more data to get
		if (moreData) {
			// Set the events api endpoint URL
			let url = `/events`;

			// Filter events based on name, hierarchy, and type options
			let queryParams = {};

			if (!allFiltersActive()) {
				let activeAlarmTypes = getActiveFilterAlarmTypes();
				if (activeAlarmTypes.length > 0) {
					queryParams['type'] = activeAlarmTypes.join(',');
				}
			}

			// Add the page number to the query parameters
			if (apiPage > 1) {
				queryParams['page'] = apiPage;
			}

			// Add the name/serial number filter to the query parameters
			if (nameFiltering && nameFiltering.search !== '') {
				queryParams['name'] = nameFiltering.search;
			}

			// Add the organization, region, facility, and building filters if they are set
			if (nameFiltering && nameFiltering.organization !== '') {
				queryParams['org'] = nameFiltering.organization;

				if (nameFiltering.region !== '') {
					queryParams['reg'] = nameFiltering.region;

					if (nameFiltering.facility !== '') {
						queryParams['fac'] = nameFiltering.facility;

						if (nameFiltering.building !== '') {
							queryParams['bldg'] = nameFiltering.building;
						}
					}
				}
			}

			// Add the exclude acknowledged filter to the query parameters
			if (nameFiltering && nameFiltering.ack_exclude) {
				queryParams['ack'] = 'true';
			}

			// Add the exclude cleared filter to the query parameters
			if (nameFiltering && nameFiltering.clear_exclude) {
				queryParams['clear'] = 'true';
			}

			// Convert the query parameters to a query string
			url +=  dictToQueryString(queryParams);

			axiosInstance.get(url).then(response => {
				if (overwrite) {
					setEvents(response.data);
				} else {
					setEvents(prevEvents => [...prevEvents, ...response.data]);
				}

				if (response.data.length >= eventsPerPage) {
					setApiPage(apiPage + 1);
					setMoreData(true);
				} else {
					setMoreData(false);
				}
			});
		}
	}

	// Update the actual filtering options when the dialog is closed
	const handleNameDialogClose = (dialogFiltering) => {
		setNameDialogOpen(false);

		setApiPage(1);
		setMoreData(true);

		setNameFiltering(dialogFiltering);
	};
	
	// Dialog menu for setting name filtering options
	function NameFilterMenu({ hierarchy, filtering, dialogOpen, handleDialogClose }) {

		const [nameFiltering, setNameFiltering] = useState(filtering ? filtering.search : '');
		const [selectedOrg, setSelectedOrg] = useState(filtering ? filtering.organization : '');
		const [selectedReg, setSelectedReg] = useState(filtering ? filtering.region : '');
		const [selectedFac, setSelectedFac] = useState(filtering ? filtering.facility : '');
		const [selectedBld, setSelectedBld] = useState(filtering ? filtering.building : '');
		const [ackExclude, setAckExclude] = useState(filtering ? filtering.ack_exclude : false);
		const [clearExclude, setClearExclude] = useState(filtering ? filtering.clear_exclude : false);

		const handleNameFilteringChange = (e) => {
			setNameFiltering(e.target.value);
		};

		const handleOrgChange = (e) => {
			setSelectedOrg(e.target.value);
			setSelectedReg("");
			setSelectedFac("");
			setSelectedBld("");
		};

		const handleRegChange = (e) => {
			setSelectedReg(e.target.value);
			setSelectedFac("");
			setSelectedBld("");
		};

		const handleFacChange = (e) => {
			setSelectedFac(e.target.value);
			setSelectedBld("");
		};

		const handleBldChange = (e) => {
			setSelectedBld(e.target.value);
		};

		const handleAckChange = (e) => {
			setAckExclude(e.target.checked);
		};

		const handleClearChange = (e) => {
			setClearExclude(e.target.checked);
		};

		const handleClose = () => {
			handleDialogClose({
				search: nameFiltering,
				organization: selectedOrg,
				region: selectedReg,
				facility: selectedFac,
				building: selectedBld,
				ack_exclude: ackExclude,
				clear_exclude: clearExclude,
			});
		};

		const DropdownPair = ({ label, value, onChange, disabled, options }) => (
			<>
				<Grid item xs={3}>
					<Box display="flex" alignItems="center" height="100%">
						{label}
					</Box>
				</Grid>
				<Tooltip title={"Filter by locations' " + label}>
					<Grid item xs={9}>
						<Select
							value={value}
							onChange={onChange}
							disabled={disabled}
							size='small'
							style={{ width: '100%' }}
						>
						<MenuItem value="">
							<em>None</em>
						</MenuItem>
						{options.map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
						</Select>
					</Grid>
				</Tooltip>
			</>
		);

		return (
			<Dialog open={dialogOpen} onClose={handleClose}>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<span>Location Filtering Options</span>
						<IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent>
					<div>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<TextField
									label="Filter by location name or serial number"
									variant="outlined"
									value={nameFiltering}
									onChange={handleNameFilteringChange}
									fullWidth
									size="small"
									sx={{ marginTop: 1 }}
								/>
							</Grid>
							<DropdownPair
								label="Organization"
								value={selectedOrg}
								onChange={handleOrgChange}
								disabled={false}
								options={Object.keys(hierarchy)}
							/>
							<DropdownPair
								label="Region"
								value={selectedReg}
								onChange={handleRegChange}
								disabled={!selectedOrg}
								options={selectedOrg ? Object.keys(hierarchy[selectedOrg]) : []}
							/>
							<DropdownPair
								label="Facility"
								value={selectedFac}
								onChange={handleFacChange}
								disabled={!selectedReg}
								options={selectedReg ? Object.keys(hierarchy[selectedOrg][selectedReg]) : []}
							/>
							<DropdownPair
								label="Building"
								value={selectedBld}
								onChange={handleBldChange}
								disabled={!selectedFac}
							options={selectedFac ? Object.keys(hierarchy[selectedOrg][selectedReg][selectedFac]) : []}
							/>
							<Tooltip title="Exclude events that have been acknowledged">
								<Grid item xs={6} key='ack_exclude'>
									<FormControlLabel
										control={
											<Switch
												checked={ackExclude}
												onChange={handleAckChange}
												name='Exclude Acknowledged'
											/>
										}
										label='Exclude Acknowledged'
										key='ack_exclude'
									/>
								</Grid>
							</Tooltip>
							<Tooltip title="Exclude events that have been cleared">
								<Grid item xs={6} key='clear_exclude'>
									<FormControlLabel
										control={
											<Switch
												checked={clearExclude}
												onChange={handleClearChange}
												name='Exclude Cleared'
											/>
										}
										label='Exclude Cleared'
										key='clear_exclude'
									/>
								</Grid>
							</Tooltip>
						</Grid>
					</div>
				</DialogContent>
			</Dialog>
		);
	}
	
	// Update the actual filtering options when the dialog is closed
	const handleTypeDialogClose = (dialogFiltering) => {
		setTypeDialogOpen(false);

		setApiPage(1);
		setMoreData(true);

		setTypeFiltering(dialogFiltering);
	};

	// Dialog menu for setting type filtering options
	function TypeFilterMenu({ filtering, dialogOpen, handleDialogClose }) {

		const [dialogFiltering, setDialogFiltering] = useState(filtering);
		
		// Return true if any filters are active
		function anyFiltersActive() {
			for (let key in dialogFiltering) {
				if (dialogFiltering[key].enabled) {
					return true;
				}
			}
			
			return false;
		}
		
		const handleClose = () => {
			if (anyFiltersActive()) {
				handleDialogClose(dialogFiltering);  // Only set the filtering if at least one filter is active
			} else {
				handleDialogClose(filtering);  // If no filters are active, close the dialog without saving the changes
			}
		};
		
		// Update the dialog's filtering options instead of the actual filtering options when a switch is toggled
		const handleToggle = (event) => {
			setDialogFiltering({
				...dialogFiltering,
				[event.target.name]: { ...dialogFiltering[event.target.name], enabled: event.target.checked },
			});
		};

		return (
			<Dialog open={dialogOpen} onClose={handleClose}>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<span>Filtering Options</span>
						<IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={2}>
						{Object.keys(dialogFiltering).map((key) => (
							<Grid item xs={6} key={key}>
								<FormControlLabel
									control={
										<Switch
											checked={dialogFiltering[key].enabled}
											onChange={handleToggle}
											name={key}
										/>
									}
									label={dialogFiltering[key].name}
									key={key}
								/>
							</Grid>
						))}
					</Grid>
				</DialogContent>
			</Dialog>
		);
	}

	const SystemNameStyle = {
		minWidth: '200px',
		width: '200px',
	};

	const SerialStyle = {
		minWidth: '100px',
		width: '100px',
	};

	const DateTimeStyle = {
		minWidth: '190px',
		width: '190px',
	};

	const ClearedStyle = {
		minWidth: '190px',
		width: '190px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const EventStyle = {
		minWidth: '400px',
		width: '50%',
	};

	const NoteStyle = {
		minWidth: '200px',
		width: '40%',
	};

	const AckStyle = {
		minWidth: '190px',
		width: '190px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const AckUserStyle = {
		minWidth: '160px',
		width: '10%',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	const RetrievedStyle = {
		minWidth: '190px',
		width: '190px',
		'@media (max-width:1280px)': {
			display: 'none',  // Hide the column on smaller windows
		}
	};

	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Toolbar>
				<Box display="flex" justifyContent="space-between" alignItems="center" width='100%'>
					{/* <Tooltip title="Refresh event data">
						<IconButton
							sx={{
								border: '1px solid',
								borderRadius: 1,
								color: 'primary.main',
							}}
							onClick={() => refreshEvents()}
						>
							<RefreshIcon />
						</IconButton>
					</Tooltip> */}
					<Box
						align="left"
						fontFamily='Saira,sans-serif'
						fontWeight='600'
						sx={{ flexGrow: 1}}
					/>
					<Tooltip title="Toggle type colorization">
						<IconButton
							sx={{
								border: '1px solid',
								borderRadius: 1,
								color: isColorized ? 'primary.main' : 'grey.500',
							}}
							onClick={() => setIsColorized(!isColorized)}
						>
							<ColorLensIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Set location name and hierarchy filtering">
						<IconButton
							sx={{ border: '1px solid', borderRadius: 1, borderColor: typeDialogOpen ? 'primary.main' : 'grey.500', margin: 1 }}
							onClick={() => setNameDialogOpen(true)}
						>
							<FilterListIcon />
							<Typography
								fontFamily='Saira,sans-serif'
								fontWeight='600'
								sx={{ marginLeft: 1 }}
							>
								Name
							</Typography>
						</IconButton>
					</Tooltip>
					<Tooltip title="Set alarm type filtering">
						<IconButton
							sx={{ border: '1px solid', borderRadius: 1, borderColor: typeDialogOpen ? 'primary.main' : 'grey.500' }}
							onClick={() => setTypeDialogOpen(true)}
						>
							<FilterListIcon />
							<Typography
								fontFamily='Saira,sans-serif'
								fontWeight='600'
								sx={{ marginLeft: 1 }}
							>
								Type
							</Typography>
						</IconButton>
					</Tooltip>
				</Box>
			</Toolbar>
			<NameFilterMenu hierarchy={hierarchy} filtering={nameFiltering} setFiltering={setNameFiltering} dialogOpen={nameDialogOpen} handleDialogClose={handleNameDialogClose}/>
			<TypeFilterMenu filtering={typeFiltering} setFiltering={setTypeFiltering} dialogOpen={typeDialogOpen} handleDialogClose={handleTypeDialogClose}/>
			<div
				id="scrollableDiv"
				style={{
					height: 'calc(100vh - 230px)',
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<InfiniteScroll
					dataLength={events.length}
					next={() => { getEventData(false) }}
					hasMore={moreData}
					scrollableTarget="scrollableDiv"
					style={{ overflow: 'visible' }}
					loader={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							Loading events...
						</Typography>
					}
					endMessage={
						<Typography fontFamily='Saira,sans-serif' fontWeight='600' sx={{ textAlign: 'center'}}>
							No more events to load.
						</Typography>
					}
				>
					<TableContainer sx={{ margin: 0, padding: 0 }}>
						<Table size="small" sx={{ margin: 0, borderSpacing: 0 }}>
							<TableHead>
								<TableRow>
									<TableCell sx={{ ...SystemNameStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										System Name
									</TableCell>
									<TableCell sx={{ ...SerialStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Serial Num
									</TableCell>
									<TableCell sx={{ ...DateTimeStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time
									</TableCell>
									<TableCell sx={{ ...ClearedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Date/Time Cleared
									</TableCell>
									<TableCell sx={{ ...EventStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Event
									</TableCell>
									<TableCell sx={{ ...NoteStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Note
									</TableCell>
									<TableCell sx={{ ...AckStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Acknowledged
									</TableCell>
									<TableCell sx={{ ...AckUserStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Ack. User
									</TableCell>
									<TableCell sx={{ ...RetrievedStyle, fontWeight: 'bold' }} size="small" padding="checkbox">
										Retrieved
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{events.map((event, index) => (
									<TableRow
										key={index}
										onClick={() => navigate(`/location/${event.location_id}`)}
										sx={{
											cursor: 'pointer',
											backgroundColor: isColorized ? (
												tinycolor(EventTypeToColor(event.alarm_type, event.number)).lighten(15).toString()
											) : ( 
												index % 2 === 0 ? '#FFFFFF' : '#F2F2F2'
											),
											'&:hover': {
												backgroundColor: isColorized ? EventTypeToColor(event.alarm_type, event.number) : '#D9D9D9',
											},
										}}
									>
										<TableCell sx={SystemNameStyle} size="small" padding="checkbox">
											{event.location_name}
										</TableCell>
										<TableCell sx={SerialStyle} size="small" padding="checkbox">
											{event.serial_number}
										</TableCell>
										<TableCell sx={DateTimeStyle} size="small" padding="checkbox">
											{formatDateTime(event.datetime)}
										</TableCell>
										<TableCell sx={ClearedStyle} size="small" padding="checkbox">
											{formatDateTime(event.clear_datetime)}
										</TableCell>
										<TableCell sx={EventStyle} size="small" padding="checkbox">
											{AlarmEventToStatusText(event)}
										</TableCell>
										<TableCell sx={NoteStyle} size="small" padding="checkbox">
											{event.note_trimmed}
										</TableCell>
										<TableCell sx={AckStyle} size="small" padding="checkbox">
											{formatDateTime(event.ack_datetime)}
										</TableCell>
										<TableCell sx={AckUserStyle} size="small" padding="checkbox">
											{event.ack_user_trimmed}
										</TableCell>
										<TableCell sx={RetrievedStyle} size="small" padding="checkbox">
											{formatDateTime(event.retrieve_datetime)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</InfiniteScroll>
			</div>
		</Paper>
	);


}